import { useEffect, useState } from 'react';
import { Download, Share, X, Plus } from 'lucide-react';
import { BottomActionSheet } from './BottomActionSheet';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DEFAULT_DISMISS_KEY = 'expenseInstallDismissed';

function isStandalone(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches
        || (navigator as unknown as { standalone?: boolean }).standalone === true;
}

function isIos(): boolean {
    return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

/**
 * "Add to Home Screen" affordance.
 *  - Android / desktop Chrome: capture beforeinstallprompt → show an Install button.
 *  - iOS Safari: one-time dismissible hint with the Share → Add to Home Screen steps.
 * Defaults are the Expense Capture wording; /bank-balance/weekly passes its own.
 */
export function InstallPrompt({
    title = 'Install Expenses',
    pitch = 'Add this app to your Home Screen for quick one-tap receipt capture:',
    dismissKey = DEFAULT_DISMISS_KEY,
}: { title?: string; pitch?: string; dismissKey?: string } = {}) {
    const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
    const [showIosHint, setShowIosHint] = useState(false);

    useEffect(() => {
        if (isStandalone()) return;

        const onBip = (e: Event) => {
            e.preventDefault();
            setDeferred(e as BeforeInstallPromptEvent);
        };
        window.addEventListener('beforeinstallprompt', onBip);

        if (isIos() && localStorage.getItem(dismissKey) !== '1') {
            setShowIosHint(true);
        }
        return () => window.removeEventListener('beforeinstallprompt', onBip);
    }, [dismissKey]);

    const install = async () => {
        if (!deferred) return;
        await deferred.prompt();
        await deferred.userChoice;
        setDeferred(null);
    };

    const dismissIos = () => {
        localStorage.setItem(dismissKey, '1');
        setShowIosHint(false);
    };

    if (deferred) {
        return (
            <button
                type="button"
                onClick={install}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 py-2.5 text-sm font-medium text-blue-700 hover:bg-blue-100"
            >
                <Download className="h-4 w-4" /> Install this app on your phone
            </button>
        );
    }

    return (
        <BottomActionSheet open={showIosHint} onClose={dismissIos} title={title}>
            <div className="space-y-3 px-1 pb-2 text-sm text-gray-700">
                <p>{pitch}</p>
                <ol className="space-y-2">
                    <li className="flex items-center gap-2"><Share className="h-4 w-4 text-blue-600" /> Tap the Share button in Safari</li>
                    <li className="flex items-center gap-2"><Plus className="h-4 w-4 text-blue-600" /> Choose “Add to Home Screen”</li>
                </ol>
                <button
                    type="button"
                    onClick={dismissIos}
                    className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl bg-gray-100 py-2.5 text-sm font-medium text-gray-600"
                >
                    <X className="h-4 w-4" /> Got it
                </button>
            </div>
        </BottomActionSheet>
    );
}
